import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

export interface MediaLightboxProps {
  /** How many items the viewer can page through. */
  count: number
  /** Which one is showing, zero-based. */
  index: number
  /** Line under the media — filename, size, whatever identifies it. */
  caption?: ReactNode
  onClose: () => void
  onIndexChange: (next: number) => void
  /** The media itself. */
  children: ReactNode
}

/**
 * The chrome around a full-screen media viewer: backdrop, counter, arrows, keyboard.
 *
 * Extracted from the map's attachment viewer so a second gallery does not re-implement Escape,
 * wrapping arrow keys and backdrop dismissal slightly differently. What varies between callers is
 * only the element in the middle — an `<img>` for an attachment, an `<img>` or `<video>` for a
 * field-device capture — so that is the one thing passed in.
 */
export function MediaLightbox({ count, index, caption, onClose, onIndexChange, children }: MediaLightboxProps) {
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        onIndexChange((index + 1) % count)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        onIndexChange((index - 1 + count) % count)
      }
    }

    document.addEventListener('keydown', handleKey)

    return () => document.removeEventListener('keydown', handleKey)
  }, [index, count, onClose, onIndexChange])

  return (
    <div
      data-testid="lightbox-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
    >
      <div onClick={e => e.stopPropagation()} className="relative max-h-[90vh] max-w-[90vw]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-8 right-0 text-white hover:text-gray-300"
        >
          <X className="h-6 w-6" />
        </button>
        <p className="absolute -top-8 left-0 text-sm text-white">
          {index + 1} of {count}
        </p>
        <div className="flex items-center gap-3">
          {count > 1 && (
            <button
              type="button"
              onClick={() => onIndexChange((index - 1 + count) % count)}
              aria-label="Previous"
              className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {children}
          {count > 1 && (
            <button
              type="button"
              onClick={() => onIndexChange((index + 1) % count)}
              aria-label="Next"
              className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </div>
        {caption && <p className="mt-2 text-center text-sm text-white">{caption}</p>}
      </div>
    </div>
  )
}
